/**
 * ─────────────────────────────────────────────────────────────────
 * Modal para crear una nueva lista de compra.
 *
 * Comportamiento por plataforma:
 *  - Móvil: Bottom sheet (desliza desde abajo) — patrón nativo Android/iOS
 *  - Escritorio: Dialog centrado estándar
 *
 * Lógica de ubicación:
 *  - "Lista personal" → NO envía idsala → backend guarda idsala = NULL
 *  - Sala concreta    → envía idsala
 * ─────────────────────────────────────────────────────────────────
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Divider,
    useTheme,
    useMediaQuery,
    Slide,
} from "@mui/material";
import { crearLista }            from "../api/listas";
import { obtenerSalasDelUsuario } from "../api/salas";

/**
 * Transición de deslizamiento desde abajo para el bottom sheet móvil.
 * Se pasa al prop TransitionComponent del Dialog.
 */
const SlideUp = (props) => <Slide direction="up" {...props} />;

export default function CrearListaModal({ open, onClose, salaId = null, onCreated = null }) {
    const navigate = useNavigate();
    const theme    = useTheme();

    /**
     * isMobile reactivo — determina si mostramos bottom sheet o dialog.
     */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [nombre,    setNombre]    = useState("");
    const [salas,     setSalas]     = useState([]);
    const [ubicacion, setUbicacion] = useState(salaId || "personal");
    const [cargando,  setCargando]  = useState(false);
    const [error,     setError]     = useState(null);

    /** Sincroniza la ubicación si se abre con una sala fija */
    useEffect(() => {
        if (open && salaId) setUbicacion(salaId);
    }, [open, salaId]);

    /** Carga las salas del usuario cuando se abre el modal */
    useEffect(() => {
        if (!open) return;
        async function cargarSalas() {
            try {
                const data = await obtenerSalasDelUsuario();
                setSalas(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error cargando salas", err);
                setSalas([]);
            }
        }
        cargarSalas();
    }, [open]);

    /** Resetea el formulario y cierra */
    const handleClose = () => {
        if (cargando) return;
        setNombre("");
        setUbicacion(salaId || "personal");
        setError(null);
        onClose();
    };

    /** Envía el formulario y crea la lista */
    const handleCrear = async () => {
        if (!nombre.trim()) return;
        setCargando(true);
        setError(null);

        try {
            const payload = { nombre: nombre.trim() };
            if (ubicacion !== "personal") payload.idsala = Number(ubicacion);

            const nueva = await crearLista(payload);

            setNombre("");
            setUbicacion(salaId || "personal");

            // Si nos pasan onCreated lo llamamos; si no, navegamos al detalle
            if (onCreated) {
                onCreated(nueva);
            } else {
                navigate(`/listas/${nueva.idlista}`);
            }
            onClose();
        } catch (err) {
            console.error("Error creando lista", err);
            setError("No se pudo crear la lista. Inténtalo de nuevo.");
        } finally {
            setCargando(false);
        }
    };

    /**
     * Contenido del formulario — compartido entre móvil y escritorio
     * para no duplicar código.
     */
    const formulario = (
        <Stack spacing={2.5}>
            <TextField
                label="Nombre de la lista"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCrear()}
                autoFocus
                fullWidth
                size={isMobile ? "medium" : "medium"}
                inputProps={{ maxLength: 60 }}
                helperText={`${nombre.length}/60`}
            />

            {/* Selector de sala — se oculta si ya viene con salaId fijo */}
            {!salaId && (
                <FormControl fullWidth>
                    <InputLabel>Ubicación</InputLabel>
                    <Select
                        label="Ubicación"
                        value={ubicacion}
                        onChange={(e) => setUbicacion(e.target.value)}
                        MenuProps={{
                            // En móvil el menú desplegable no debe salirse de pantalla
                            PaperProps: {
                                sx: { maxHeight: 220, borderRadius: 3 },
                            },
                        }}
                    >
                        <MenuItem value="personal">
                            <em>Lista personal</em>
                        </MenuItem>
                        {salas.map((s) => (
                            <MenuItem key={s.idsala} value={s.idsala}>
                                {s.nombre}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {error && (
                <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                    {error}
                </Typography>
            )}
        </Stack>
    );

    /* ── MÓVIL: Bottom Sheet ─────────────────────────────────────── */
    if (isMobile) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={SlideUp}
                fullWidth
                fullScreen={false}
                PaperProps={{
                    sx: {
                        // Ancla en la parte inferior de la pantalla
                        position:     "fixed",
                        bottom:       0,
                        left:         0,
                        right:        0,
                        m:            0,
                        width:        "100%",
                        maxWidth:     "100%",
                        borderRadius: "20px 20px 0 0",
                        // Respeta la safe area de iOS/Android
                        pb:           "env(safe-area-inset-bottom)",
                        // Altura máxima: no ocupa más de 80% de pantalla
                        maxHeight:    "80vh",
                        overflow:     "auto",
                    },
                }}
                // Centra el backdrop en toda la pantalla
                sx={{ "& .MuiDialog-container": { alignItems: "flex-end" } }}
            >
                {/* Pastilla de arrastre visual (no funcional, solo UX hint) */}
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
                    <Box sx={{ width: 40, height: 4, borderRadius: 99, bgcolor: "divider" }} />
                </Box>

                <Box sx={{ px: 2.5, pt: 1, pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        Nueva lista
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        Dale un nombre y elige dónde guardarla
                    </Typography>
                </Box>

                <Divider />

                <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                    {formulario}
                </Box>

                {/* Botones de acción — full width en móvil */}
                <Box sx={{ px: 2.5, pb: 2, display: "flex", gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        disabled={cargando}
                        sx={{
                            borderRadius:  2,
                            fontWeight:    700,
                            textTransform: "none",
                            minHeight:     48,  // área táctil mínima
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCrear}
                        disabled={!nombre.trim() || cargando}
                        sx={{
                            borderRadius:  2,
                            fontWeight:    700,
                            textTransform: "none",
                            minHeight:     48,
                        }}
                    >
                        {cargando ? <CircularProgress size={20} color="inherit" /> : "Crear lista"}
                    </Button>
                </Box>
            </Dialog>
        );
    }

    /* ── ESCRITORIO: Dialog centrado estándar ────────────────────── */
    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 800 }}>Crear nueva lista</DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    {formulario}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={cargando}
                    sx={{ textTransform: "none" }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCrear}
                    disabled={!nombre.trim() || cargando}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                >
                    {cargando ? <CircularProgress size={20} color="inherit" /> : "Crear lista"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}